import { ForumError, forumError, staleResponse } from './forumErrors';
import { forumId, mapBootstrap, mapSections, mapTopics, mapThread, mapSearch, mapActivity, mapPending } from '../models/forumMappers';
import { queryKey, topicsQuery, threadTarget, searchQuery, activityQuery, pendingQuery } from '../models/forumQuery';
import { createForumCache } from '../services/forumCache';

// Dependency injection keeps repositories independent from Axios and Vue.
// getIdentity must include UID + auth/permission revision from the shared session.
export function createForumRepository({ http, getIdentity, now = Date.now }) {
  const pages = createForumCache({ now });
  const structure = createForumCache({ maxEntries: 1, ttl: 300000, now });
  let identityKey = null, accessKey = null;
  function reset() { pages.invalidate(); structure.invalidate(); accessKey = null; identityKey = null; }
  function identity() {
    const current = getIdentity() || {};
    const key = JSON.stringify([current.uid || null, current.session || null, current.permissions || null]);
    if (key !== identityKey) { reset(); identityKey = key; }
    if (!forumId(current.uid)) throw new ForumError('authentication_required', 'Inicia sesión para acceder al foro.', 401);
    return { key, uid: String(current.uid) };
  }
  function assertIdentity(expected) { if (identity().key !== expected.key) throw staleResponse(); }
  async function read(path, params, map, expected) {
    try {
      const response = await http.get(`/forum/api/v2/${path}`, { params });
      assertIdentity(expected);
      return map(response.data);
    } catch (error) {
      assertIdentity(expected);
      const failure = forumError(error);
      if ([401, 403].includes(failure.status)) { pages.invalidate(); structure.invalidate(); accessKey = null; }
      throw failure;
    }
  }
  async function bootstrap({ refresh = false } = {}) {
    const expected = identity();
    const model = await structure.read(expected.key, () => read('bootstrap', {}, mapBootstrap, expected), { refresh });
    assertIdentity(expected);
    if (model.uid !== expected.uid) { reset(); throw staleResponse(); }
    if (accessKey !== model.accessKey) { pages.invalidate(); accessKey = model.accessKey; }
    return model;
  }
  async function page(resource, params, mapper, tags, options = {}) {
    const expected = identity();
    const context = await bootstrap();
    assertIdentity(expected);
    if (resource === 'need-response' && context.capabilities.canModerate !== true) throw new ForumError('pending_denied', 'No tienes permiso para consultar los mensajes pendientes.', 403);
    const key = queryKey(resource, { ...params, uid: context.uid, access: context.accessKey });
    const result = await pages.read(key, () => read(resource, params, mapper, expected), { ...options, tags });
    assertIdentity(expected);
    if (context.accessKey !== accessKey) throw staleResponse();
    if (params.page !== undefined && result.page && result.page.page !== params.page) {
      // A deletion may clamp the requested page. Seed the canonical URL from
      // this authoritative result, so replacing it does not fetch twice.
      const canonical = queryKey(resource, { ...params, page: result.page.page, uid: context.uid, access: context.accessKey });
      pages.seed(canonical, result, { tags });
      assertIdentity(expected);
      if (context.accessKey !== accessKey) throw staleResponse();
    }
    return result;
  }
  return {
    bootstrap, reset,
    sections: options => page('sections', {}, mapSections, ['topics'], options),
    topics: (input, options) => page('topics', topicsQuery(input), mapTopics, ['topics'], options),
    activity: (input, options) => page('activity', activityQuery(input), mapActivity, ['activity'], options),
    pending: (input, options) => page('need-response', pendingQuery(input), mapPending, ['pending'], options),
    thread: (id, input, options) => {
      const nid = forumId(id);
      if (!nid) return Promise.reject(new ForumError('invalid_input', 'El enlace del hilo no es válido.', 400));
      return page(`threads/${nid}`, threadTarget(input), mapThread, [`thread:${nid}`], options);
    },
    search: (input, options) => {
      const query = searchQuery(input);
      if (!query.q) { identity(); return Promise.resolve({ hits: [], page: { page: 1, pages: 1, limit: 20, total: 0 } }); }
      return page('search', query, mapSearch, ['search'], options);
    },
    // Future mutations provide affected tags; an old in-flight result cannot undo them.
    invalidate: (tags, options) => pages.invalidate((key, entryTags) => entryTags.some(tag => tags.includes(tag)), options)
  };
}
